import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { ContactDetailsProjectsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import ContactsProjectsList from 'modules/finance/components/Contacts/ContactsProjectsList'
import { useContactProjectListPermissions } from 'modules/finance/hooks/useContactProjectListPermissions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { MissingFeatureEmptyState } from 'modules/project_management/components/upsells/MissingFeatureEmptyState'
import { MissingModuleEmptyState } from 'modules/project_management/components/upsells/MissingModuleEmptyState'

const ContactsDetailsProjectsPage: React.FC = () => {
  const { contactId } = useParams()
  const { company, canRequestUpsell } = useCurrentSession()

  const { data, loading, error } = useQuery(ContactDetailsProjectsPageDocument, {
    variables: {
      id: Number(contactId),
      companyId: company.id,
      featureIds: [
        Features.projects_management,
        Features.projects_clients,
        Features.projects_costs,
      ],
    },
  })

  const contact = data?.finance?.contact

  const hasClientFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available

  const hasProjectsManagementFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_management
  )?.available

  const seeProjectsPage = data?.projectManagement._policies.pagesSeeProjectsRoot.can ?? false

  // When they dont have projects module at all
  const seeProjectsUpsell = !hasProjectsManagementFeature
  // When they have some projects features
  const seeProjectsUpgrade = hasProjectsManagementFeature && !hasClientFeature
  const canSeeProjectsPageContent = seeProjectsPage && hasClientFeature

  const canSeeProjectsPageAsUpsell = canRequestUpsell && (seeProjectsUpsell || seeProjectsUpgrade)

  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'projects',
      projects_upsell: canSeeProjectsPageAsUpsell,
    })
  }, [canSeeProjectsPageAsUpsell])

  const { loading: loadingProjectListPermissions, policies } = useContactProjectListPermissions()

  const canSeePage = canSeeProjectsPageAsUpsell || canSeeProjectsPageContent

  return (
    <ResponsivePage.Body
      authorized={canSeePage}
      error={error}
      loading={loading || loadingProjectListPermissions}
      variant="full"
    >
      {canSeeProjectsPageAsUpsell ? (
        <Box alignItems="center" justifyContent="center" style={{ height: '50vh' }}>
          {seeProjectsUpsell ? (
            <MissingModuleEmptyState requestSource="projects_sales_clients_projects_tab" />
          ) : (
            <MissingFeatureEmptyState
              featureRequested={Features.projects_clients}
              requestSource="projects_sales_clients_projects_tab"
              description={i18n.t('project_management.spending.table.upsell_message.legacy_plan')}
            />
          )}
        </Box>
      ) : (
        contact && <ContactsProjectsList policies={policies} contact={contact} />
      )}
    </ResponsivePage.Body>
  )
}

export default ContactsDetailsProjectsPage
